import { format } from 'date-fns'
import { parseDateLocal } from '~/util/date'
import { UNIT_TO_BUCKET_FN, type GranularityUnit } from './dataPipeline'
import { aggregateRatioRows } from './aggregateRatioRows'
import type { WalkTimelineRow } from '~/api/endpoints/walks'

/** Structural minimum a row needs to be foldable. WalkTimelineRow and the export layer's
 *  FlatInputRow both satisfy it (FlatInputRow after the Task-7 widening). */
export type FoldableRow = {
  kpi_type: string
  time?: string
  value_type: string | null
  dims: Record<string, string | null> | null
  value: number | null
  numerator?: number
  denominator?: number
}

/** Row-level display-grain fold (spec §4.2) — the export layer's and strip replay's twin of
 *  the chart's two engines. Volume: group-sum per (dims, value_type, folded bucket) using the
 *  SAME bucket fn the chart folds with (UNIT_TO_BUCKET_FN — shared kernel, so values match
 *  summariseByUnit to the cent). Ratio: delegates to aggregateRatioRows (Σnum/Σdenom, NEVER
 *  mean-of-ratios; its numerator guard throws on non-ratio rows — kept, no silent fallback).
 *  Callers decide the identity skip (unit === wire bucket → don't call). */
export function foldRowsToUnit<R extends FoldableRow>(
  rows: R[],
  unit: GranularityUnit,
  engine: 'volume' | 'ratio',
): R[] {
  for (const r of rows) {
    if (r.time == null) {
      throw new Error('foldRowsToUnit: row has no time — fold called on a time-less walk family')
    }
  }
  if (engine === 'ratio') {
    // Safe cast: time verified above; aggregateRatioRows reads only FoldableRow-compatible
    // fields and returns rows of the same shape (components dropped, value = Σnum/Σdenom).
    return aggregateRatioRows(rows as unknown as WalkTimelineRow[], unit, false) as unknown as R[]
  }
  const bucketFn = UNIT_TO_BUCKET_FN[unit]
  type Acc = { row: R; hasValue: boolean }
  const byKey = new Map<string, Acc>()
  for (const r of rows) {
    const bucket = format(bucketFn(parseDateLocal(r.time!)), 'yyyy-MM-dd')
    const key = `${JSON.stringify(r.dims ?? null)}\0${r.value_type ?? ''}\0${bucket}`
    const cur = byKey.get(key)
    if (!cur) {
      byKey.set(key, { row: { ...r, time: bucket }, hasValue: r.value != null })
    } else {
      if (r.value != null) {
        cur.row.value = (cur.hasValue ? cur.row.value! : 0) + r.value
        cur.hasValue = true
      }
    }
  }
  return [...byKey.values()]
    .map((a) => (a.hasValue ? a.row : { ...a.row, value: null }))
    .sort(
      (a, b) =>
        String(a.time).localeCompare(String(b.time)) ||
        JSON.stringify(a.dims ?? null).localeCompare(JSON.stringify(b.dims ?? null)) ||
        String(a.value_type ?? '').localeCompare(String(b.value_type ?? '')),
    )
}
